import { useDownloadReport } from "@features/cx/consumer-onboarding/hooks";
import { ServiceOrderDialogue } from "@features/cx/shared/components/ServiceOrderDialogue";
import { useServiceOrders } from "@features/cx/shared/hooks/useServiceOrder";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { toast } from "@shared/ui/use-toast";
import { Eye, Plus, Wrench } from "lucide-react";
import { useMemo, useState } from "react";

interface ServiceOrdersTabProps {
  consumerId: string;
  consumerName: string;
  requestId: string;
  remoteUtilityId: number;
  consumerAccountNumber: string;
  currentStatus?: string;
}

export function ServiceOrdersTab({
  consumerId,
  consumerName,
  requestId,
  remoteUtilityId,
  consumerAccountNumber,
  currentStatus,
}: ServiceOrdersTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const viewReport = useDownloadReport();

  // Memoize the filters to prevent infinite API calls
  const filters = useMemo(
    () => ({
      remote_utility_id: remoteUtilityId,
      request_type: "service-order",
      request_id: requestId,
      // Add other filters as needed
    }),
    [remoteUtilityId, requestId]
  );

  // Use the service orders hook
  const { serviceOrders } = useServiceOrders(filters, {
    enabled: !!remoteUtilityId && !!requestId,
  });

  const handleCreateNew = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleServiceOrderSuccess = () => {
    // Refetch service orders after successful creation
    // refetch();
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "created":
        return "outline";
      case "completed":
        return "outline";
      case "scheduled":
        return "secondary";
      case "in progress":
        return "default";
      case "canceled":
        return "destructive";
      case "pending":
        return "secondary";
      default:
        return "outline";
    }
  };

  const handleViewReport = async (reportId: any) => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      const blob = await viewReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });
      
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
    } catch (error) {
      toast({
        title: "Error viewing report",
        description: "Failed to open the inspection report.",
        variant: "destructive",
      });
    }
  };

  const isCreateDisabled = useMemo(() => {
    if (!currentStatus) return false;
    const statusUpper = String(currentStatus).toUpperCase().trim();
    return (
      statusUpper === "COMPLETED" ||
      statusUpper === "REJECTED" ||
      statusUpper === "CANCELLED" ||
      statusUpper.includes("COMPLET")
    );
  }, [currentStatus]);

  return (
    <>
     <Card>
  <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
    <CardTitle className="card-heading-text">Service Orders</CardTitle>
    <Button
      variant="outline"
      size="sm"
      onClick={handleCreateNew}
      disabled={isCreateDisabled}
    >
      <Plus className="h-4 w-4 mr-2" />
      Create New Service Order
    </Button>
  </CardHeader>

  <CardContent>
    {serviceOrders.length === 0 ? (
      <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
        <Wrench className="h-12 w-12 mb-4 opacity-50" />
        <p>No service orders found</p>
        <p className="text-sm">Create a new service order to get started</p>
      </div>
    ) : (
      <div className="space-y-4">
        {serviceOrders.map((order, index) => (
          <div
            key={index}
            className=" p-4 rounded-md border space-y-4 relative"
          >
            {/* Header */}
            <div className="flex items-center gap-3">
              <span className="bg-blue-100 text-blue-700 font-semibold rounded px-3 py-1 text-sm tracking-wide">
                {order.jobCardNo}
              </span>
              <span className="font-medium">{order.sopName ?? "NA"}</span>
              <Badge
                className="absolute right-4 top-4 px-3 py-1 text-sm font-medium"
                variant={getStatusBadgeVariant(order.statusDisplay ?? "NA")}
              >
                {order.statusDisplay}
              </Badge>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <p className="text-xs text-muted-foreground">Scheduled Date</p>
                <p className="text-sm font-medium">
                  {order.requestAvailabilityDate ?? "NA"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created On</p>
                <p className="text-sm font-medium">
                  {order.createdDate || "NA"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created By</p>
                <p className="text-sm font-medium">
                  {order.createdUserRemoteName || "NA"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Assigned To</p>
                <p className="text-sm font-medium">
                  {order.fieldForceName || "NA"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Assigned On</p>
                <p className="text-sm font-medium">
                  {order.assignDate || "NA"}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewReport(order.id)}
              >
                <Eye className="h-4 w-4 mr-2" />
                View Report
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </CardContent>
</Card>

      <ServiceOrderDialogue
        consumerAccountNumber={consumerAccountNumber}
        consumerId={consumerId}
        consumerName={consumerName}
        requestId={requestId}
        remoteUtilityId={remoteUtilityId}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleServiceOrderSuccess}
      />
    </>
  );
}
